import BottomSheet from "components/foundation/BottomSheet/BottomSheet";
import Button from "components/foundation/Button";
import Chip from "components/foundation/Chip/Chip";
import Tab from "components/foundation/Tab";
import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { Table } from "./Table";
import Select from "components/foundation/Select/Select";
import { REGION_DATA } from "../PageFilterPannel/FilterRegion";
import { useSwipe } from "features/common/hooks/useSwipe";

interface TableTagInputMobileProps {
  children: React.ReactNode;
  initialValue?: SelectedTags;
  data: {
    label: string;
    value: string;
    type: "checkbox" | "dropdown";
    options: {
      title: string;
      value: string;
    }[];
  }[];
  isRegion?: boolean;
  // TODO: 시간이 없어서 추상화 하지 못한 부분
  onSubmit?: (data: SelectedTags) => void;
}

export type SelectedTags = {
  region?: SelectedRegion;
} & {
  [key: string]: { title: string; value: string }[];
};

export type SelectedRegion =
  | {
      region: string;
      city: string | undefined;
    }
  | undefined;

const TableTagInputMobile = ({
  children,
  data,
  isRegion,
  initialValue,
  onSubmit,
}: TableTagInputMobileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTagTitle, setSelectedTagTitle] = useState(
    isRegion ? "region" : data[0].value,
  );

  const [selectedTags, setSelectedTags] = useState<SelectedTags>({});
  const [selectedRegion, setSelectedRegion] = useState<SelectedRegion>();

  const handleChipClick = (tag: { title: string; value: string }) => {
    setSelectedTags((prev) => {
      const currentTags = prev[selectedTagTitle] || [];
      const exists = currentTags.some((t) => t.value === tag.value);

      const updatedTags = exists
        ? currentTags.filter((t) => t.value !== tag.value) // Remove tag if it exists
        : [...currentTags, tag]; // Add tag if it doesn't exist

      if (updatedTags.length === 0) {
        const { [selectedTagTitle]: _, ...rest } = prev; // Remove key if array is empty
        return rest;
      }

      return {
        ...prev,
        [selectedTagTitle]: updatedTags,
      };
    });
  };

  const handleConfirm = () => {
    setIsOpen(false);
    onSubmit &&
      onSubmit({ ...selectedTags, region: selectedRegion } as SelectedTags);
  };

  useEffect(() => {
    setSelectedTags(initialValue ?? {});
  }, [initialValue]);

  // 전체 탭 목록 생성
  const allTabs = useMemo(() => {
    const tabs = [...data];
    if (isRegion) {
      tabs.unshift({
        label: "지역",
        value: "region",
        type: "dropdown" as const,
        options: [],
      });
    }
    return tabs;
  }, [data, isRegion]);

  const goToPreviousTab = () => {
    const currentIndex = allTabs.findIndex(
      (tab) => tab.value === selectedTagTitle,
    );
    if (currentIndex > 0) {
      setSelectedTagTitle(allTabs[currentIndex - 1].value);
    }
  };

  const goToNextTab = () => {
    const currentIndex = allTabs.findIndex(
      (tab) => tab.value === selectedTagTitle,
    );
    if (currentIndex < allTabs.length - 1) {
      setSelectedTagTitle(allTabs[currentIndex + 1].value);
    }
  };

  const swipeHandlers = useSwipe({
    swipeLeft: goToNextTab,
    swipeRight: goToPreviousTab,
    threshold: 50,
  });

  return (
    <>
      <TableInputMobileButton
        variant={
          Object.values(selectedTags).length > 0 ? "primary" : "tertiary"
        }
        size="small"
        onClick={() => setIsOpen(true)}
      >
        {children}
      </TableInputMobileButton>
      {isOpen && (
        <BottomSheet title="태그" isOpen={isOpen} setOpen={setIsOpen}>
          <ContentHeader>
            {isRegion && (
              <Tab
                selected={selectedTagTitle === "region"}
                onClick={() => setSelectedTagTitle("region")}
              >
                지역
              </Tab>
            )}

            {data.map((item) => {
              return (
                <Tab
                  key={item.value}
                  selected={selectedTagTitle === item.value}
                  onClick={() => setSelectedTagTitle(item.value)}
                >
                  {item.label}
                </Tab>
              );
            })}
          </ContentHeader>
          <ContentLayout {...swipeHandlers}>
            {selectedTagTitle === "region" && (
              <RegionWrapper>
                <Select
                  list={Object.keys(REGION_DATA).map((region) => ({
                    label: region,
                    value: region,
                  }))}
                  placeholder="시/도 선택"
                  value={selectedRegion?.region}
                  onValueChange={(value) => {
                    setSelectedRegion({
                      city: undefined,
                      region: value,
                    });
                  }}
                />

                <Select
                  list={
                    REGION_DATA[
                      selectedRegion?.region as keyof typeof REGION_DATA
                    ]?.map((city) => ({
                      label: city,
                      value: city,
                    })) ?? []
                  }
                  value={selectedRegion?.city}
                  placeholder="시/군/구 선택"
                  onValueChange={(value) => {
                    setSelectedRegion(
                      (prev) =>
                        ({
                          ...prev,
                          city: value,
                        }) as SelectedRegion,
                    );
                  }}
                  disabled={!!selectedRegion?.region}
                />
              </RegionWrapper>
            )}
            <div>
              {data
                .find((i) => i.value === selectedTagTitle)
                ?.options.map((option) => {
                  return (
                    <Chip
                      key={option.title}
                      isSelected={
                        selectedTags[selectedTagTitle]?.some(
                          (tag) => tag.value === option.value,
                        ) || false
                      }
                      onClick={() => handleChipClick(option)}
                    >
                      {option.title}
                    </Chip>
                  );
                })}
            </div>
          </ContentLayout>
          <Button
            size="large"
            style={{
              width: "100%",
            }}
            onClick={handleConfirm}
          >
            확인
          </Button>
        </BottomSheet>
      )}
    </>
  );
};

export default TableTagInputMobile;

const ContentHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.6rem;
`;

const ContentLayout = styled.div`
  min-height: 25vh;
  max-height: 25vh;
  overflow-y: auto;
  touch-action: pan-y pinch-zoom; /* 세로 스크롤과 줌은 허용하지만 가로 스와이프는 커스텀 처리 */

  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

const TableInputMobileButton = styled(Table.Button)`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  padding: 0.4rem 1rem;
`;

const RegionWrapper = styled.div`
  display: flex;
  gap: 1rem;

  > button {
    flex: 1;
  }
  > div {
    flex: 1;
  }
`;
